// @safetyhub/report-composer — 작성 페이지 위젯 트리 ↔ 바인딩 sync (reconcile)
//
// composerWidgetTree(저작 트리, 정본)와 deriveComposerWidgets 파생 풀을 대조해:
//  - unplaced: 바인딩은 있으나 트리에 아직 배치되지 않은 입력 위젯(팔레트/풀 대기)
//  - orphanKeys: 트리에 배치됐으나 대응 바인딩이 삭제된 입력 위젯의 bindingKey(제거 후보)
// 를 산출한다. 트리 자체는 변형하지 않는다(정본 보존 — 배치/설정 안정).
//
// 결정: 2026-07-08-composer-writeform-editing-structure §5-1
//   "저작 트리가 정본 + 바인딩 sync(파생은 seed/prune으로 격하)".
//   실제 배치(unplaced→tree 이동)·orphan 제거는 저작 UI(단계 3)의 사용자 액션.

import type { ComposerWidget, InputWidget } from '@safetyhub/shared';

/** sync 결과 — 트리는 무변형, 미배치/orphan은 파생 메타. */
export interface ComposerWidgetSyncResult {
  /** 저작 트리(정본, 무변형). orphan 여부는 orphanKeys로 판별. */
  tree: ComposerWidget[];
  /** 바인딩 존재하나 트리 미배치인 입력 위젯(파생 순서 유지, bindingKey dedup). */
  unplaced: InputWidget[];
  /** 트리에 배치됐으나 바인딩 삭제된 입력 위젯의 bindingKey(트리 순회 순서). */
  orphanKeys: string[];
}

/**
 * 트리를 순회하며 배치된 입력 위젯의 bindingKey 집합을 수집한다(group children 재귀).
 * design 위젯(heading/separator/spacer)은 bindingKey가 없어 제외.
 * 등록형(widget==='registered')·트리 소유(treeOwned — 카탈로그 직결 등 직접 생성) 위젯은
 * 파생 풀에서 오지 않는 트리 소유물이라 제외(orphan 오판 방지).
 */
export function collectPlacedInputKeys(tree: ComposerWidget[]): Set<string> {
  const keys = new Set<string>();
  const treeOwnedWidget = (w: InputWidget): boolean => w.widget === 'registered' || !!w.treeOwned;
  const walk = (nodes: ComposerWidget[]): void => {
    for (const w of nodes) {
      if (w.kind === 'input') {
        if (treeOwnedWidget(w)) continue; // 트리 소유(파생 대조 제외)
        keys.add(w.bindingKey);
      } else if (w.kind === 'group') {
        walk(w.children);
      } else if (w.kind === 'branch') {
        // branch: 투명 컨테이너 — 슬롯 children input의 bindingKey는 본문 스코프 placed 키(group 동형).
        // 전 슬롯 하강(구조적 배치 — 현재 활성 여부와 무관).
        for (const slot of w.branches) walk(slot.children);
      } else if (w.kind === 'formTable') {
        // 표 고정 행 셀 위젯(중첩 InputWidget)의 bindingKey도 배치된 것으로 카운트(풀 중복 방지).
        for (const row of w.rows) {
          if (row.type !== 'fixed') continue;
          for (const cell of row.cells) {
            if (cell.widget && !treeOwnedWidget(cell.widget)) keys.add(cell.widget.bindingKey);
          }
        }
      } else if (w.kind === 'repeat') {
        // repeat: over(=DataSourceDef.key)를 배치로 집계 — 파생 풀의 dataSourceRows 위젯
        // (bindingKey=def.key)이 미배치로 오판돼 중복 배치 제안되는 것 방지. children은
        // **행 상대키**(행 객체 내부 필드)라 본문 placed가 아니므로 하강하지 않는다(모달 동형).
        keys.add(w.over);
      }
      // modalTrigger: 의도적으로 스킵 — 모달 children input의 bindingKey는 **모달 스코프** 전용이라
      // 본문 placed로 세면 안 되고(세면 unplaced 목록이 잘못 빔), modalTrigger 자신의 bindingKey는
      // 입력이 아니라 모달 UI 상태 저장 키라 placed 집계 대상이 아니다. children 순회하지 않음.
    }
  };
  walk(tree);
  return keys;
}

/**
 * 저작 트리 ↔ 파생 풀 reconcile.
 * @param tree composerWidgetTree(미지정이면 빈 트리 — 전 풀이 unplaced).
 * @param pool deriveComposerWidgets 결과(입력 위젯 풀; 비-input은 무시).
 */
export function syncComposerWidgets(
  tree: ComposerWidget[] | undefined,
  pool: ComposerWidget[],
): ComposerWidgetSyncResult {
  const safeTree = tree ?? [];
  const placed = collectPlacedInputKeys(safeTree);

  const poolInputs = pool.filter((w): w is InputWidget => w.kind === 'input');
  const poolKeys = new Set(poolInputs.map((w) => w.bindingKey));

  // unplaced: 파생 순서 유지 + bindingKey dedup(같은 키 중복 파생 방지).
  const seen = new Set<string>();
  const unplaced: InputWidget[] = [];
  for (const w of poolInputs) {
    if (placed.has(w.bindingKey) || seen.has(w.bindingKey)) continue;
    seen.add(w.bindingKey);
    unplaced.push(w);
  }

  // orphan: 트리에 배치됐으나 파생 풀에 대응 바인딩 없음.
  const orphanKeys: string[] = [];
  for (const k of placed) {
    if (!poolKeys.has(k)) orphanKeys.push(k);
  }

  return { tree: safeTree, unplaced, orphanKeys };
}
